import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MappingResponse } from '../../services/api';

export interface UseCanvasFocusOptions {
  mappingResponse?: MappingResponse;
  activeWorkbookId?: string;
  initialSourceFilter?: string[];
  initialTargetFilter?: string[];
}

export function useCanvasFocus({ mappingResponse, activeWorkbookId, initialSourceFilter, initialTargetFilter }: UseCanvasFocusOptions) {
  const [focusModeEnabled, setFocusModeEnabled] = useState(false);
  const [focusedTargetIds, setFocusedTargetIds] = useState<Set<string>>(new Set());
  const [focusedSourceIds, setFocusedSourceIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedGroupsInitialized, setExpandedGroupsInitialized] = useState(false);
  const [fitViewTrigger, setFitViewTrigger] = useState(0);

  const handleToggleFocusTarget = useCallback((tableId: string) => {
    setFocusedTargetIds((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId);
      else next.add(tableId);
      return next;
    });
    setFitViewTrigger((prev) => prev + 1);
  }, []);

  const handleToggleFocusSource = useCallback((alias: string) => {
    setFocusedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias);
      else next.add(alias);
      return next;
    });
    setFitViewTrigger((prev) => prev + 1);
  }, []);

  const handleClearFocus = useCallback(() => {
    setFocusedTargetIds(new Set());
    setFocusedSourceIds(new Set());
    setFitViewTrigger((prev) => prev + 1);
  }, []);

  const handleToggleFocusMode = useCallback(() => {
    setFocusModeEnabled((prev) => !prev);
  }, []);

  const handleToggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Auto-expand all groups when mapping data first loads or workbook changes.
  // This ensures tables start expanded (pre-Phase 1 behavior) so users see fields + edges.
  // Users can manually collapse individual groups if desired.
  useEffect(() => {
    if (!mappingResponse?.mappings?.length) return;
    if (expandedGroupsInitialized) return;

    const allGroupIds = new Set<string>();
    const sourceSet = new Set<string>();
    const targetSet = new Set<string>();

    mappingResponse.mappings.forEach((m) => {
      const source = (m.source_file ?? 'source').trim() || 'source';
      const target = (m.target_table ?? 'target').trim() || 'target';
      sourceSet.add(source);
      targetSet.add(target);
    });

    sourceSet.forEach((alias) => allGroupIds.add(`group:source:${alias}`));
    targetSet.forEach((table) => allGroupIds.add(`group:target:${table}`));

    // Phase 4a: Initialize origin group expand states.
    // Classify source aliases into origin groups using the :: prefix convention.
    // Groups with <=2 tables start expanded; groups with 3+ start collapsed.
    const originChildren = new Map<string, string[]>();
    sourceSet.forEach((alias) => {
      const sepIdx = alias.indexOf('::');
      const originKey = sepIdx > 0 ? alias.substring(0, sepIdx) : '__file_uploads__';
      const children = originChildren.get(originKey) ?? [];
      children.push(alias);
      originChildren.set(originKey, children);
    });
    if (originChildren.size > 1) {
      originChildren.forEach((children, originKey) => {
        if (children.length <= 2) {
          allGroupIds.add(`origin:${originKey}`);
        }
      });
    }

    setExpandedGroups(allGroupIds);
    setExpandedGroupsInitialized(true);

    // Initialize focus from metadata filters (Phase 0d)
    if (initialSourceFilter?.length && focusedSourceIds.size === 0) {
      setFocusedSourceIds(new Set(initialSourceFilter.map(s => s.toLowerCase())));
    }
    if (initialTargetFilter?.length && focusedTargetIds.size === 0) {
      setFocusedTargetIds(new Set(initialTargetFilter.map(t => t.toLowerCase())));
    }
  }, [mappingResponse, expandedGroupsInitialized]);

  // Reset state when workbook changes
  useEffect(() => {
    setExpandedGroups(new Set());
    setExpandedGroupsInitialized(false);
  }, [activeWorkbookId]);

  const availableGroups = useMemo(() => {
    if (!mappingResponse) return [];

    const sourceSet = new Set<string>();
    const targetSet = new Set<string>();

    (mappingResponse.mappings ?? []).forEach((m) => {
      const source = (m.source_file ?? 'source').trim() || 'source';
      const target = (m.target_table ?? 'target').trim() || 'target';
      sourceSet.add(source);
      targetSet.add(target);
    });

    // Zero-mapping batches: list tables from schema snapshots in the navigator.
    if (sourceSet.size === 0 && targetSet.size === 0) {
      const meta = mappingResponse.metadata;
      const sourceTables = meta?.source_schema_set_snapshot?.tables;
      if (sourceTables) {
        Object.keys(sourceTables).forEach((key) => sourceSet.add(key));
      } else {
        meta?.source_schema_signature?.sources?.forEach((src) => {
          const name = (src.source ?? '').trim();
          if (name) sourceSet.add(name);
        });
      }
      const targetTables =
        mappingResponse.transformation_plan?.target_schema_snapshot?.tables
        ?? meta?.target_schema_snapshot?.tables
        ?? meta?.schema_context?.target_schema_snapshot?.tables;
      if (targetTables) {
        Object.keys(targetTables).forEach((key) => targetSet.add(key));
      }
    }

    const groups: Array<{ id: string; label: string; role: 'source' | 'target' }> = [];
    Array.from(targetSet)
      .sort()
      .forEach((id) => {
        groups.push({ id, label: id, role: 'target' });
      });
    Array.from(sourceSet)
      .sort()
      .forEach((id) => {
        groups.push({ id, label: id, role: 'source' });
      });

    return groups;
  }, [mappingResponse]);

  return {
    focusModeEnabled,
    setFocusModeEnabled,
    focusedTargetIds,
    focusedSourceIds,
    expandedGroups,
    setExpandedGroups,
    fitViewTrigger,
    setFitViewTrigger,
    handleToggleFocusTarget,
    handleToggleFocusSource,
    handleClearFocus,
    handleToggleFocusMode,
    handleToggleGroup,
    availableGroups,
  };
}
